/**
 * CsvImportBar — shows the result of a CSV preview call with valid/duplicate/invalid counts
 * and a Commit button. Generic: works for phone and email allowlists.
 */

interface CsvImportBarProps {
  totalRows: number;
  validCount: number;
  duplicateCount: number;
  invalidCount: number;
  loading?: boolean;
  error?: string | null;
  onCommit: () => void;
  onDiscard: () => void;
}

export function CsvImportBar({
  totalRows,
  validCount,
  duplicateCount,
  invalidCount,
  loading,
  error,
  onCommit,
  onDiscard,
}: CsvImportBarProps) {
  return (
    <div
      style={{
        background: "var(--bg-input)",
        border: "1px dashed var(--border)",
        borderRadius: 8,
        padding: "10px 12px",
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        lineHeight: 1.7,
        color: "var(--text-muted)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div>
        <span>Preview — {totalRows} row{totalRows !== 1 ? "s" : ""} scanned. </span>
        <span style={{ color: "var(--success)" }}>✓ {validCount} valid</span>
        {duplicateCount > 0 && (
          <span style={{ color: "var(--warning)" }}>{" · "}⚠ {duplicateCount} dup</span>
        )}
        {invalidCount > 0 && (
          <span style={{ color: "var(--error)" }}>{" · "}✗ {invalidCount} invalid</span>
        )}
      </div>

      {error && (
        <div style={{ color: "var(--error)", fontSize: 10, fontFamily: "var(--font-sans)" }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={onCommit}
          disabled={loading || validCount === 0}
          style={{
            padding: "4px 12px",
            borderRadius: 6,
            border: "1px solid var(--accent)",
            background: "var(--accent)",
            color: "white",
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            fontWeight: 600,
            cursor: loading || validCount === 0 ? "not-allowed" : "pointer",
            opacity: loading || validCount === 0 ? 0.5 : 1,
          }}
        >
          {loading ? "Committing…" : `Commit ${validCount} entr${validCount !== 1 ? "ies" : "y"}`}
        </button>
        <button
          onClick={onDiscard}
          disabled={loading}
          style={{
            padding: "4px 12px",
            borderRadius: 6,
            border: "1px solid var(--border)",
            background: "transparent",
            color: "var(--text-muted)",
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}
